<template>
  <div>
    <vue-slideout-panel
      v-model="showInfoDialog"
      :widths="['40%']"
      close-html="<i class='fal fa-lg fa-times'></i>"
      @close="showInfoDialog = false"
    >
      <receipt-info
        :receipt-object="receiptInfo"
        :show-print-button="false"
        @canceled="cancelParkedReceipt($event)"
        @imported="importParkedReceipt($event)"
      />
    </vue-slideout-panel>

    <barcode-scanner @scan-init="scanBarcode" />
    <v-text-field
      v-if="includeSearch"
      ref="search"
      v-model="conditions.search"
      :label="$t('Search')"
      class="mb-6"
      filled
      hide-details
      prepend-inner-icon="far fa-search"
      single-line
    />

    <v-data-table
      :headers="headers"
      :items="receipts"
      :loading="$wait.is('fetching receipts')"
      :options.sync="pagination"
      :server-items-length="nrOfReceipts"
      v-bind="dataTableProps"
    >
      <template v-slot:item="props">
        <tr
          :key="props.item.uuid"
          style="cursor: pointer"
          @click="showInfo(props.item)"
        >
          <td>{{ props.item.parkedName }}</td>
          <td>{{ props.item.nr }}</td>
          <td>{{ _.get(props, 'item.store.name') }}</td>
          <td>{{ _.get(props, 'item.customer.fullName', $t('Walk In')) }}</td>
          <td>{{ props.item.created | moment("D MMMM YYYY H:mm") }}</td>
          <td>{{ props.item.totalInclVatRounded | money }}</td>
          <td class="text-right">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  :loading="$wait.is(`importing receipt ${props.item.uuid}`)"
                  class="ma-0"
                  icon
                  small
                  v-on="on"
                  @click.stop="importParkedReceipt(props.item.uuid)"
                >
                  <v-icon small>fal fa-external-link</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('Load {0}', [$t('Receipt')]) }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  :loading="$wait.is(`canceling receipt ${props.item.uuid}`)"
                  class="ma-0"
                  icon
                  small
                  v-on="on"
                  @click.stop="cancelParkedReceipt(props.item.uuid)"
                >
                  <v-icon small>fal fa-trash-alt</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('Delete {0}', [$t('Receipt')]) }}</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>
    <receipts-filter-speed-dial />
  </div>
</template>

<script>
import receiptService from '@/services/receipts'
import camelcaseKeys from 'camelcase-keys'
import BarcodeScanner from '@/components/BarcodeScanner'
import StatusChip from '@/components/chips/StatusChip'
import ReceiptInfo from '@/components/receipts/ReceiptInfo'
import ReceiptsFilterSpeedDial from '@/components/receipts/ReceiptsFilterSpeedDial'
import { RECEIPT_PENDING } from '@/utils/statuses/receipt'
import VueSlideoutPanel from 'vue-slideout-panel'
import { VDataTableDefaultProps as dataTableProps } from '@/utils/default-props/v-data-table'

export default {
  name: 'ParkedReceiptsTable',

  components: {
    BarcodeScanner,
    StatusChip,
    ReceiptInfo,
    ReceiptsFilterSpeedDial,
    VueSlideoutPanel
  },

  props: {
    includeSearch: {
      default: false,
      type: Boolean
    },
    receiptStatus: {
      default: RECEIPT_PENDING,
      type: Number,
      required: false
    }
  },

  data () {
    return {
      headers: [
        {
          text: this.$t('Name'),
          value: 'Receipts.parked_name',
          sortable: true
        },
        {
          text: this.$t('Order Nr.'),
          value: 'Receipts.nr',
          sortable: true
        },
        {
          text: this.$t('Store'),
          value: 'Stores.name',
          sortable: true
        },
        {
          text: this.$t('Customer'),
          value: 'Customers.last_name',
          sortable: true
        },
        {
          text: this.$t('Date'),
          value: 'Receipts.created',
          sortable: true
        },
        {
          text: this.$t('Total'),
          value: 'Receipts.total_incl_vat_rounded',
          sortable: true
        },
        {
          text: '',
          value: 'id',
          sortable: false,
          width: '150px'
        }
      ],
      search: '',
      receipts: [],
      pagination: {},
      nrOfReceipts: 0,
      showInfoDialog: false,
      receiptInfo: null,
      conditions: {},
      dataTableProps
    }
  },

  watch: {
    // fetch receipts when requested page or sorting changed
    pagination (value) {
      this.updateResults()
    },
    // fetch receipts when search has changed
    'conditions.search': function (search) {
      this.updateResults()
    }
  },

  methods: {
    scanBarcode (barcode) {
      this.conditions.search = barcode
    },
    updateResults () {
      this.$wait.start('fetching receipts')
      this.showInfoDialog = false
      this.receipts = []

      this.$set(this.conditions, 'status', this.receiptStatus)
      this.$set(this.conditions, 'parked', true)

      receiptService.pagination(this.conditions, this.pagination)
        .then((data) => {
          if (data.data) {
            this.receipts = camelcaseKeys(data.data.receipts, { deep: true })
            this.nrOfReceipts = data.data.total_count
          }
        })
        .finally((data) => this.$wait.end('fetching receipts'))
    },
    showInfo (item) {
      this.receiptInfo = item
      this.showInfoDialog = true
    },
    cancelParkedReceipt (receiptUuid) {
      this.$swal({
        title: this.$t('Are you sure?'),
        type: 'warning',
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: this.$t('Yes'),
        showCancelButton: true,
        reverseButtons: true
      })
        .then((result) => {
          if (result.value) {
            this.$wait.start(`canceling receipt ${receiptUuid}`)
            return this.$store.dispatch('RECEIPT/CANCEL_RECEIPT', receiptUuid)
              .then((data) => {
                this.helpers.toastSuccess(data.message)
                this.updateResults()
              })
              .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
              .finally((data) => this.$wait.end(`canceling receipt ${receiptUuid}`))
          }
        })
    },
    importParkedReceipt (receiptUuid) {
      this.$wait.start(`importing receipt ${receiptUuid}`)
      return this.$store.dispatch('RECEIPT/IMPORT_RECEIPT', receiptUuid)
        .then((data) => {
          this.helpers.toastSuccess(data.message)
          this.$router.push({ name: 'Cart' })
        })
        .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
        .finally((data) => this.$wait.end(`importing receipt ${receiptUuid}`))
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/receipts/ParkedReceiptsTable.vue